import { useEffect, useMemo, useState } from 'react'
import * as customersApi from '@/api/customers'
import { useAuth } from '@/auth/AuthContext'
import { TableFieldsPanel } from '@/components/FieldEditor'
import { TableImportPanel } from '@/components/TableImportPanel'
import { WideCellDisplay, WideCellEditor } from '@/components/WideCellEditor'
import { generatePassword } from '@/lib/generatePassword'

function withPreviewKeys(cols) {
  return (cols || []).map((c, i) => ({
    ...c,
    key:
      c.key ||
      `draft-${i}-${String(c.label || 'col')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'col'}`,
  }))
}

function emptyDraft(columns) {
  const values = {}
  for (const col of columns) values[col.key] = ''
  return { values, notes: '' }
}

async function copyText(text) {
  if (!text) return false
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    /* fall through */
  }
  return false
}

function SecretCell({ value, canReveal }) {
  const [show, setShow] = useState(false)
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    const ok = await copyText(value)
    if (!ok) return
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  if (!value) return <span className="text-slate-400">—</span>
  if (!canReveal) {
    return <span className="font-mono text-sm text-brand">********</span>
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className="font-mono text-sm">{show ? value : '••••••••'}</span>
      <button
        type="button"
        className="text-xs text-brand-muted underline"
        onClick={() => setShow((s) => !s)}
      >
        {show ? 'Hide' : 'Show'}
      </button>
      <button
        type="button"
        className="text-xs text-brand-muted underline"
        onClick={handleCopy}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </span>
  )
}

function SecretFieldInput({ value, onChange, placeholder, className }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    const ok = await copyText(value)
    if (!ok) return
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="flex min-w-[12rem] flex-col gap-1">
      <input
        className={className}
        type="text"
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="text-xs font-medium text-brand underline"
          onClick={() => onChange(generatePassword())}
          title="Create a what3words-style password (word.word.word + number + special)"
        >
          Create password
        </button>
        <button
          type="button"
          className="text-xs text-brand-muted underline disabled:opacity-40"
          disabled={!value}
          onClick={handleCopy}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

function IconEdit({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.1 2.1 0 1 1 2.97 2.97L8.25 18.04l-4 1 1-4 11.612-11.553z" />
    </svg>
  )
}

function IconEyeOff({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  )
}

function IconEye({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
    </svg>
  )
}

function IconTrash({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0V4.394c0-.538-.214-1.055-.595-1.436A1.958 1.958 0 0 0 13.72 2.25H10.28c-.512 0-1.004.203-1.366.565A2.03 2.03 0 0 0 8.32 4.394V5.25m7.5 0h-7.5" />
    </svg>
  )
}

function IconCloseTask({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  )
}

function IconReopenTask({ className = 'h-3.5 w-3.5' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.5M2.25 18.75a8.25 8.25 0 0 0 14.559 3.014l.191-.24M21.75 5.25A8.25 8.25 0 0 0 7.191 2.236l-.191.24" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 5.25v4.098m0 0H17.652" />
    </svg>
  )
}

/** Tables with a Due date column (or ToDo in the title/slug) get close/reopen. */
function isCloseableTable(category, columns) {
  if ((columns || []).some((c) => c.input_type === 'due_date')) return true
  const slug = String(category?.slug || '').toLowerCase()
  const title = String(category?.title || '').toLowerCase()
  return slug.includes('todo') || title.includes('todo') || title.includes('to-do') || title.includes('to do')
}

function isRowClosed(row) {
  return Boolean(row?.closed_at)
}

const iconBtnClass =
  'rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand'

function isDateColumn(col) {
  return col.input_type === 'expiry' || col.input_type === 'due_date'
}

function isWideTextColumn(col) {
  return col.input_type === 'text' && Boolean(col.wide)
}

function inputTypeForColumn(col) {
  if (col.input_type === 'secret') return 'text'
  if (col.input_type === 'url') return 'url'
  if (isDateColumn(col)) return 'date'
  return 'text'
}

/** Display ISO YYYY-MM-DD as a UK-friendly date. */
function formatExpiryValue(raw) {
  const text = String(raw || '').trim()
  if (!text) return null
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    }
  }
  return text
}

function CellDisplay({ col, value, canViewSecrets }) {
  if (col.input_type === 'secret') {
    return <SecretCell value={value} canReveal={canViewSecrets} />
  }
  if (col.input_type === 'url' && value) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="text-sky-800 underline break-all"
      >
        {value}
      </a>
    )
  }
  if (isDateColumn(col)) {
    const formatted = formatExpiryValue(value)
    return formatted || <span className="text-slate-400">—</span>
  }
  if (isWideTextColumn(col)) {
    return <WideCellDisplay value={value} />
  }
  return value || <span className="text-slate-400">—</span>
}

function ValueField({ col, value, onChange, placeholder, className }) {
  if (col.input_type === 'secret') {
    return (
      <SecretFieldInput
        className={className}
        placeholder={placeholder}
        value={value || ''}
        onChange={onChange}
      />
    )
  }
  if (isWideTextColumn(col)) {
    return (
      <WideCellEditor
        value={value || ''}
        placeholder={placeholder}
        onChange={onChange}
      />
    )
  }
  return (
    <input
      className={className}
      type={inputTypeForColumn(col)}
      placeholder={placeholder}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function compareRowValues(a, b, key) {
  const av = (a.values?.[key] ?? '').toString()
  const bv = (b.values?.[key] ?? '').toString()
  return av.localeCompare(bv, undefined, {
    sensitivity: 'base',
    numeric: true,
  })
}

export function StructuredTable({
  customerId,
  category,
  canEdit,
  tableKey = '',
  columns: columnsProp,
  onColumnsChange,
}) {
  const { canViewSecrets } = useAuth()
  const savedColumns = columnsProp?.length ? columnsProp : category.columns || []
  const [fieldsDraft, setFieldsDraft] = useState(null)
  const columns = fieldsDraft?.length ? withPreviewKeys(fieldsDraft) : savedColumns
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [draft, setDraft] = useState(() => emptyDraft(savedColumns))
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingFields, setEditingFields] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [importing, setImporting] = useState(false)
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('asc')
  const [showClosed, setShowClosed] = useState(false)

  const columnKeys = useMemo(() => columns.map((c) => c.key).join(','), [columns])
  const closeable = useMemo(
    () => isCloseableTable(category, columns),
    [category, columns],
  )
  const closedCount = useMemo(
    () => (closeable ? rows.filter((r) => isRowClosed(r)).length : 0),
    [closeable, rows],
  )

  const displayRows = useMemo(() => {
    let list = rows
    if (closeable && !showClosed) {
      list = list.filter((r) => !isRowClosed(r))
    }
    if (!sortKey || !columns.some((c) => c.key === sortKey)) return list
    const dir = sortDir === 'desc' ? -1 : 1
    return [...list].sort((a, b) => dir * compareRowValues(a, b, sortKey))
  }, [rows, columns, sortKey, sortDir, closeable, showClosed])

  useEffect(() => {
    setDraft(emptyDraft(columns))
  }, [category.slug, tableKey, columnKeys])

  useEffect(() => {
    if (sortKey && !columns.some((c) => c.key === sortKey)) {
      setSortKey(null)
      setSortDir('asc')
    }
  }, [columns, sortKey])

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const data = await customersApi.listRows(customerId, category.slug, {
          tableKey: tableKey || undefined,
        })
        if (!cancelled) setRows(data)
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load rows.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [customerId, category.slug, tableKey])

  function setDraftValue(key, value) {
    setDraft((prev) => ({ ...prev, values: { ...prev.values, [key]: value } }))
  }

  async function handleAdd(e) {
    e?.preventDefault?.()
    setSaving(true)
    setError('')
    try {
      const created = await customersApi.createRow(customerId, category.slug, {
        values: draft.values,
        notes: draft.notes || null,
        table_key: tableKey || '',
      })
      setRows((prev) => [...prev, created])
      setDraft(emptyDraft(columns))
      setShowAddForm(false)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not add row.',
      )
    } finally {
      setSaving(false)
    }
  }

  function startEdit(row) {
    setEditingId(row.id)
    setEditDraft({
      values: { ...emptyDraft(columns).values, ...(row.values || {}) },
      notes: row.notes || '',
      hidden_from_customer: row.hidden_from_customer,
    })
  }

  async function saveEdit(rowId) {
    setSaving(true)
    setError('')
    try {
      const updated = await customersApi.updateRow(customerId, category.slug, rowId, {
        values: editDraft.values,
        notes: editDraft.notes || null,
        hidden_from_customer: editDraft.hidden_from_customer,
      })
      setRows((prev) => prev.map((r) => (r.id === rowId ? updated : r)))
      setEditingId(null)
      setEditDraft(null)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save row.',
      )
    } finally {
      setSaving(false)
    }
  }

  async function toggleRowHidden(row) {
    try {
      const updated = await customersApi.updateRow(customerId, category.slug, row.id, {
        hidden_from_customer: !row.hidden_from_customer,
      })
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)))
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not update visibility.',
      )
    }
  }

  async function setRowClosed(row, closed) {
    setError('')
    try {
      const updated = await customersApi.updateRow(customerId, category.slug, row.id, {
        is_closed: closed,
      })
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)))
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : closed
            ? 'Could not close task.'
            : 'Could not reopen task.',
      )
    }
  }

  async function removeRow(rowId) {
    if (!window.confirm('Delete this row?')) return
    try {
      await customersApi.deleteRow(customerId, category.slug, rowId)
      setRows((prev) => prev.filter((r) => r.id !== rowId))
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not delete row.',
      )
    }
  }

  async function handleImport(rows) {
    setImporting(true)
    setError('')
    try {
      const result = await customersApi.importRows(customerId, category.slug, {
        tableKey: tableKey || '',
        rows: rows.map((r) => ({
          values: r.values,
          table_key: tableKey || '',
        })),
      })
      setRows((prev) => [...prev, ...(result.rows || [])])
      setShowImport(false)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not import rows.',
      )
    } finally {
      setImporting(false)
    }
  }

  const inputClass =
    'w-full min-w-[7rem] rounded border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand'

  if (loading) {
    return <p className="text-sm text-brand-muted">Loading rows…</p>
  }

  return (
    <div className="space-y-3">
      {error && (
        <p className="text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      {(canEdit || closeable) && (
        <div className="relative z-20 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && onColumnsChange && !editingFields && (
              <button
                type="button"
                onClick={() => {
                  setEditingFields(true)
                  setShowImport(false)
                }}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-brand hover:bg-slate-50"
              >
                Edit Fields
              </button>
            )}
            {canEdit && columns.length > 0 && !showImport && (
              <button
                type="button"
                onClick={() => {
                  setShowImport(true)
                  setEditingFields(false)
                }}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-brand hover:bg-slate-50"
              >
                Import
              </button>
            )}
            {closeable && (
              <label className="ml-auto flex items-center gap-2 text-xs text-brand-muted">
                <input
                  type="checkbox"
                  checked={showClosed}
                  onChange={(e) => setShowClosed(e.target.checked)}
                />
                Show closed
                {closedCount > 0 ? ` (${closedCount})` : ''}
              </label>
            )}
          </div>
          {editingFields && onColumnsChange && (
            <TableFieldsPanel
              columns={savedColumns}
              onDraftChange={(fields) => setFieldsDraft(fields)}
              onCancel={() => {
                setFieldsDraft(null)
                setEditingFields(false)
              }}
              onSave={async (fields) => {
                await onColumnsChange(fields)
                setFieldsDraft(null)
                setEditingFields(false)
              }}
            />
          )}
          {showImport && (
            <TableImportPanel
              columns={columns}
              importing={importing}
              onCancel={() => setShowImport(false)}
              onImport={handleImport}
            />
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-300 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
          <thead className="bg-slate-100">
            <tr>
              {columns.map((col) => {
                const active = sortKey === col.key
                return (
                  <th
                    key={col.key}
                    className="whitespace-nowrap px-3 py-2 font-semibold text-brand-muted"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(col.key)}
                      className={[
                        'inline-flex items-center gap-1 hover:text-brand',
                        active ? 'text-brand' : '',
                      ].join(' ')}
                      aria-sort={
                        active
                          ? sortDir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      {col.label}
                      {col.input_type === 'due_date' ? (
                        <span className="rounded bg-sky-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-sky-800">
                          due
                        </span>
                      ) : null}
                      {col.input_type === 'expiry' ? (
                        <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                          expiry
                        </span>
                      ) : null}
                      {isWideTextColumn(col) ? (
                        <span className="rounded bg-slate-200 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                          notes
                        </span>
                      ) : null}
                      <span className="text-[10px] tabular-nums" aria-hidden>
                        {active ? (sortDir === 'asc' ? '▲' : '▼') : '◇'}
                      </span>
                    </button>
                  </th>
                )
              })}
              {canEdit && (
                <th className="w-0 px-2 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {rows.length === 0 && !showAddForm && (
              <tr>
                <td
                  colSpan={Math.max(columns.length, 1) + (canEdit ? 1 : 0)}
                  className="px-3 py-4 text-center text-brand-muted"
                >
                  No rows yet{canEdit ? ' — use Add Field or Import.' : '.'}
                </td>
              </tr>
            )}

            {displayRows.map((row) => {
              const isEditing = editingId === row.id
              const hidden = Boolean(row.hidden_from_customer)
              const closed = isRowClosed(row)
              return (
                <tr
                  key={row.id}
                  className={[
                    hidden ? 'bg-amber-50/40' : '',
                    closed ? 'bg-slate-50 opacity-70' : '',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={[
                        'px-3 py-2 align-middle text-brand',
                        isWideTextColumn(col) ? 'min-w-[16rem] max-w-xl' : '',
                      ].join(' ')}
                    >
                      {isEditing ? (
                        <ValueField
                          col={col}
                          className={inputClass}
                          value={editDraft?.values?.[col.key] || ''}
                          onChange={(next) =>
                            setEditDraft((prev) => ({
                              ...prev,
                              values: { ...prev.values, [col.key]: next },
                            }))
                          }
                        />
                      ) : (
                        <CellDisplay
                          col={col}
                          value={row.values?.[col.key]}
                          canViewSecrets={canViewSecrets}
                        />
                      )}
                    </td>
                  ))}
                  {canEdit && (
                    <td className="w-0 whitespace-nowrap px-2 py-1.5 align-middle">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => saveEdit(row.id)}
                            className="text-xs font-semibold text-brand underline"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(null)
                              setEditDraft(null)
                            }}
                            className="text-xs text-brand-muted underline"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-0.5">
                          {closeable ? (
                            closed ? (
                              <button
                                type="button"
                                onClick={() => setRowClosed(row, false)}
                                className={iconBtnClass}
                                title="Reopen task"
                                aria-label="Reopen task"
                              >
                                <IconReopenTask />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setRowClosed(row, true)}
                                className={`${iconBtnClass} text-emerald-600 hover:text-emerald-800`}
                                title="Close task"
                                aria-label="Close task"
                              >
                                <IconCloseTask />
                              </button>
                            )
                          ) : null}
                          <button
                            type="button"
                            onClick={() => startEdit(row)}
                            className={iconBtnClass}
                            title="Edit"
                            aria-label="Edit"
                          >
                            <IconEdit />
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleRowHidden(row)}
                            className={iconBtnClass}
                            title={hidden ? 'Show to customer' : 'Hide from customer'}
                            aria-label={hidden ? 'Show to customer' : 'Hide from customer'}
                          >
                            {hidden ? <IconEye /> : <IconEyeOff />}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeRow(row.id)}
                            className={iconBtnClass}
                            title="Delete"
                            aria-label="Delete"
                          >
                            <IconTrash />
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}

            {canEdit && showAddForm && (
              <tr className="bg-slate-50/80">
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={[
                      'px-3 py-2 align-middle',
                      isWideTextColumn(col) ? 'min-w-[16rem] max-w-xl' : '',
                    ].join(' ')}
                  >
                    <ValueField
                      col={col}
                      className={inputClass}
                      placeholder={col.label}
                      value={draft.values?.[col.key] || ''}
                      onChange={(next) => setDraftValue(col.key, next)}
                    />
                  </td>
                ))}
                <td className="w-0 whitespace-nowrap px-2 py-1.5 align-middle">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      disabled={saving || columns.length === 0}
                      onClick={handleAdd}
                      className="text-xs font-semibold text-brand underline disabled:opacity-50"
                    >
                      {saving ? 'Adding…' : 'Add'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddForm(false)
                        setDraft(emptyDraft(columns))
                      }}
                      className="text-xs text-brand-muted underline"
                    >
                      Cancel
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {canEdit && !showAddForm && (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="text-sm font-medium text-brand underline"
        >
          Add Field
        </button>
      )}
    </div>
  )
}
